import { ProjectLinkCreateDialog } from '@/components/ProjectLinkCreateDialog';
import { Button } from '@/components/ui/button';
import { DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getPlaceholderAvatar } from '@/core/avatar';
import { execute } from '@/graphql/execute';
import { deleteProjectLinkMutation } from '@/mutations/project.mutation';
import { useDialog } from '@/providers/DialogProvider';
import { getProjectLinksQuery } from '@/queries/project.query';
import { Copy, Plus, Trash } from '@mynaui/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import { formatDateTime } from './BatchList';
import { Badge } from './ui/badge';

interface ProjectLinkItemProps {
  link: any;
  projectId: string;
  onCopy: (url: string) => void;
  onDelete: (id: string) => void;
  isLinkExpired: (expiresAt: string) => boolean;
  deletePending: boolean;
}

function ProjectLinkItem({ link, projectId, onCopy, onDelete, isLinkExpired, deletePending }: ProjectLinkItemProps) {
  const url = `${window.location.origin}/shared/${projectId}?token=${link.token}`;
  return (
    <div
      key={link.id}
      className={`p-2 border rounded-xl bg-slate-50/40  dark:bg-stone-900/40 border-slate-200/70 dark:border-stone-800/70`}>
      <div className='flex items-start justify-between'>
        <div className='space-y-2 flex-1'>
          <div className='flex items-center gap-2'>
            <div
              className='flex-1 bg-slate-100 dark:bg-stone-800/60 px-2.5 h-8 items-center flex rounded-md cursor-copy'
              onClick={() => onCopy(url)}>
              <div className='text-xs font-mono line-clamp-1 break-words font-medium rounded-sm'>{url}</div>
            </div>
            <Button
              size='sm'
              variant='ghost'
              className='size-8'
              onClick={() => onCopy(url)}
              disabled={isLinkExpired(link.expiresAt)}>
              <Copy className='size-4 stroke-2' />
            </Button>
            <Button
              size='sm'
              variant='ghost'
              className='size-8 text-rose-500 hover:text-rose-600'
              onClick={() => onDelete(link.id)}
              disabled={deletePending}>
              <Trash className='size-4 stroke-2' />
            </Button>
          </div>
          <div className='text-xs font-medium items-center text-slate-700 dark:text-stone-400 flex gap-2'>
            <Badge
              className='dark:bg-stone-800/70 dark:border-stone-800/70 dark:text-white'
              variant={link.status === 'ACTIVE' ? 'success' : link.status === 'EXPIRED' ? 'destructive' : 'outline'}>
              {link.status}
            </Badge>
            <span className='text-slate-900 dark:text-white max-w-32 overflow-ellipsis text-ellipsis whitespace-nowrap'>
              {link.name || 'Untitled'}
            </span>
            &middot;
            <span>{formatDateTime(link.createdAt)}</span>&middot;
            <span>{formatDateTime(link.expiresAt)}</span>
            <div className='flex-1'></div>
            <span className='flex items-center gap-1'>
              Created by{' '}
              <img
                src={
                  link.createdBy?.avatar?.url || getPlaceholderAvatar(link.createdBy?.email || link.createdBy?.id || '')
                }
                alt={link.createdBy?.name || link.createdBy?.email || 'Unknown'}
                className='size-4 rounded-full object-cover border border-slate-200/70 dark:border-stone-800/70'
              />{' '}
              {link.createdBy?.name || link.createdBy?.email || 'Unknown'}
            </span>
          </div>
        </div>
        <div className='flex items-center'></div>
      </div>
    </div>
  );
}

interface ProjectShareDialogContentProps {
  projectId: string;
}

export function ProjectShareDialogContent({ projectId }: ProjectShareDialogContentProps) {
  const { openDialog, dismissActiveDialog } = useDialog();
  const queryClient = useQueryClient();

  // Fetch existing project links
  const { data: projectLinks, isLoading } = useQuery({
    queryKey: ['projectLinks', projectId],
    queryFn: () => execute(getProjectLinksQuery, { projectId }),
    enabled: !!projectId,
  });

  const deleteLinkMutation = useMutation({
    mutationFn: async (linkId: string) => {
      return execute(deleteProjectLinkMutation, { id: linkId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectLinks', projectId] });
      toast.success('Project link deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete project link');
    },
  });

  const _handleDeleteLink = async (linkId: string) => {
    if (confirm('Are you sure you want to delete this project link?')) {
      await deleteLinkMutation.mutateAsync(linkId);
    }
  };

  const _handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Share link copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy link to clipboard');
    }
  };

  const _handleCreateNewLink = () => {
    openDialog(<ProjectLinkCreateDialog projectId={projectId} />);
  };

  const isLinkExpired = (expiresAt: string) => {
    return dayjs(expiresAt).isBefore(dayjs());
  };

  const _handleClose = () => {
    dismissActiveDialog();
  };

  const activeLinks = projectLinks?.projectLinks?.filter((link: any) => link.status === 'ACTIVE');
  const expiredLinks = projectLinks?.projectLinks?.filter((link: any) => link.status === 'EXPIRED');

  return (
    <>
      <DialogHeader>
        <DialogTitle>Share Project</DialogTitle>
      </DialogHeader>

      <div className='space-y-6'>
        {/* Create new link button */}
        <div className='flex justify-between items-center'>
          <div>
            <h3 className='font-semibold text-slate-900 dark:text-stone-200'>Project Links</h3>
            <p className='text-xs font-medium text-slate-500 dark:text-stone-200/70'>
              Manage public links to the project.
            </p>
          </div>
          <Button onClick={_handleCreateNewLink} size='icon' className='size-7 p-0'>
            <Plus className='size-5 stroke-2' />
          </Button>
        </div>

        {/* Existing links list */}
        <div className='space-y-4'>
          {isLoading ? (
            <div className='text-sm text-muted-foreground'>Loading links...</div>
          ) : projectLinks?.projectLinks?.length === 0 ? (
            <div className='text-sm text-muted-foreground'>No shareable links created yet.</div>
          ) : (
            <div className='flex flex-col gap-2'>
              {activeLinks?.map((link: any) => (
                <ProjectLinkItem
                  key={link.id}
                  link={link}
                  projectId={projectId}
                  onCopy={_handleCopyLink}
                  onDelete={_handleDeleteLink}
                  isLinkExpired={isLinkExpired}
                  deletePending={deleteLinkMutation.isPending}
                />
              ))}
              {!!expiredLinks?.length && (
                <div className='text-sm font-medium text-slate-500 dark:text-stone-200/70 mt-4 flex items-center justify-between'>
                  <span>Expired Links</span>
                </div>
              )}
              {expiredLinks?.map((link: any) => (
                <ProjectLinkItem
                  key={link.id}
                  link={link}
                  projectId={projectId}
                  onCopy={_handleCopyLink}
                  onDelete={_handleDeleteLink}
                  isLinkExpired={isLinkExpired}
                  deletePending={deleteLinkMutation.isPending}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <DialogFooter>
        <Button variant='outline' onClick={_handleClose}>
          Close
        </Button>
      </DialogFooter>
    </>
  );
}
